/**
 * Chunked resumable PUTs against a GCS session URI. On any failure the true offset is asked back from the
 * session, so a dropped connection resumes instead of restarting: the promise on the creator screen.
 */
import { UPLOAD_CHUNK_BYTES, UPLOAD_RETRY_DELAY_MS, UPLOAD_RETRY_LIMIT } from '@studio/contracts';

const COMPLETE = -1;

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Where the session actually is: next byte offset, or COMPLETE when GCS says the object is finished. */
async function sessionOffset(sessionUri: string, total: number): Promise<number> {
  const res = await fetch(sessionUri, { method: 'PUT', headers: { 'Content-Range': `bytes */${total}` } });
  if (res.ok) return COMPLETE;
  if (res.status === 308) {
    const range = res.headers.get('Range');
    return range ? Number(range.slice(range.lastIndexOf('-') + 1)) + 1 : 0;
  }
  throw new Error(`upload session query: ${res.status}`);
}

/**
 * Drops this tab is still sending. The files go from the browser straight to the bucket, so a tab closed
 * mid-drop strands every file not yet sent; the browser asks before it lets that happen. Held at module
 * level so leaving the Upload page for another screen keeps the guard while the drop finishes.
 */
let unfinishedDrops = 0;
const warnUnfinished = (e: BeforeUnloadEvent) => {
  e.preventDefault();
};

/** Guard the tab for one drop; the returned release is safe to call more than once. */
export function holdTabOpen(): () => void {
  if (unfinishedDrops++ === 0) window.addEventListener('beforeunload', warnUnfinished);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--unfinishedDrops === 0) window.removeEventListener('beforeunload', warnUnfinished);
  };
}

/** A cancelled drop stops mid-file: the chunk in flight is abandoned and nothing is retried. */
export async function putResumable(sessionUri: string, file: File, onProgress: (sentBytes: number) => void, signal?: AbortSignal): Promise<void> {
  let offset = 0;
  let failures = 0;
  while (offset < file.size) {
    signal?.throwIfAborted();
    const end = Math.min(offset + UPLOAD_CHUNK_BYTES, file.size);
    try {
      const res = await fetch(sessionUri, {
        method: 'PUT',
        headers: { 'Content-Range': `bytes ${offset}-${end - 1}/${file.size}` },
        body: file.slice(offset, end),
        signal: signal ?? null,
      });
      if (res.status === 308) {
        const range = res.headers.get('Range');
        offset = range ? Number(range.slice(range.lastIndexOf('-') + 1)) + 1 : end;
      } else if (res.ok) {
        offset = file.size;
      } else {
        throw new Error(`upload chunk: ${res.status}`);
      }
      failures = 0;
      onProgress(offset);
    } catch (err) {
      if (signal?.aborted) throw err;
      failures += 1;
      if (failures > UPLOAD_RETRY_LIMIT) throw err instanceof Error ? err : new Error(String(err));
      await delay(UPLOAD_RETRY_DELAY_MS * failures);
      const at = await sessionOffset(sessionUri, file.size).catch(() => offset);
      offset = at === COMPLETE ? file.size : at;
      onProgress(Math.min(offset, file.size));
    }
  }
}
